/**
 * Cognito's error names, translated into keys — not into sentences.
 *
 * This file deliberately contains no user-facing text. An earlier version of the same
 * idea in the mobile app held a map of Italian strings, which meant switching the app
 * to English left the auth errors in Italian and nobody noticed until a screenshot
 * arrived. The dictionary owns words; this owns only the mapping.
 *
 * Note what is *not* distinguished: "no such user" and "wrong password" both become
 * `invalid`. The user pool has preventUserExistenceErrors on for the same reason —
 * a login form that tells you which addresses are registered is a list of who works
 * here, handed to anyone who asks.
 */

import type { TranslationKey } from '@/i18n';

type AuthErrorKey = Extract<TranslationKey, `auth.error.${string}`>;

/**
 * Keyed on the `name` Amplify attaches, which is stable across versions in a way the
 * message is not.
 */
const BY_NAME: Record<string, AuthErrorKey> = {
  NotAuthorizedException: 'auth.error.invalid',
  UserNotFoundException: 'auth.error.invalid',
  UserNotConfirmedException: 'auth.error.invalid',

  CodeMismatchException: 'auth.error.code',
  ExpiredCodeException: 'auth.error.code',
  AliasExistsException: 'auth.error.code',

  LimitExceededException: 'auth.error.attempts',
  TooManyRequestsException: 'auth.error.attempts',
  TooManyFailedAttemptsException: 'auth.error.attempts',

  UserDisabledException: 'auth.error.disabled',

  InvalidPasswordException: 'auth.error.password',
  InvalidParameterException: 'auth.error.password',

  NetworkError: 'auth.error.network',

  NoPasskeyException: 'auth.error.noPasskey',
  // The browser's passkey dialog was closed, timed out, or the device has none for here.
  NotAllowedError: 'auth.error.passkeyCancelled',
  PasskeyAuthenticationCanceled: 'auth.error.passkeyCancelled',
  PasskeyRegistrationCanceled: 'auth.error.passkeyCancelled',
  PasskeyNotSupported: 'auth.error.passkeyUnsupported',
  PasskeyAlreadyExists: 'auth.error.passkeyExists',
  RelyingPartyMismatch: 'auth.error.passkeyUnsupported',
  // Usually: this device holds no passkey for this address.
  PasskeyRetrievalFailed: 'auth.error.passkeyCancelled',
  PasskeyRegistrationFailed: 'auth.error.passkeyCancelled',
  PasskeyOperationAborted: 'auth.error.passkeyCancelled',
  WebAuthnNotEnabledException: 'auth.error.passkeyUnsupported',
  WebAuthnCredentialNotSupportedException: 'auth.error.passkeyUnsupported',
  WebAuthnRelyingPartyMismatchException: 'auth.error.passkeyUnsupported',
  WebAuthnOriginNotAllowedException: 'auth.error.passkeyUnsupported',
  WebAuthnChallengeNotFoundException: 'auth.error.passkeyCancelled',
};

/**
 * The underlying reason, for the small print under a passkey error. Several causes share
 * one sentence ("cannot use passkeys here"); the code is what tells them apart when
 * someone sends a screenshot.
 */
export function authErrorCode(error: unknown): string | null {
  const e = error as { name?: string; underlyingError?: { name?: string } } | null;
  const inner = e?.underlyingError?.name;
  return [e?.name, inner].filter(Boolean).join(' / ') || null;
}

export function authErrorKey(error: unknown): AuthErrorKey {
  const name = (error as { name?: string } | null)?.name;
  if (name && BY_NAME[name]) return BY_NAME[name];

  // A failed fetch arrives as a TypeError with no useful name. Calling that "something
  // went wrong" sends someone hunting for a password problem they do not have.
  if (error instanceof TypeError) return 'auth.error.network';

  return 'auth.error.unknown';
}
